import { PencilIcon } from '@src/components/icons/PencilIcon';
import { CogIcon } from '@src/components/icons/CogIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { OrganizerPanelBoxLink } from './OrganizerPanelBoxLink';
import { DEFAULT_GAME_SEASON } from '@src/theme/season';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { CustomQuestionBuilder } from './CustomQuestionBuilder';
import { trackThemeIconSelected } from '@src/utils/themeAnalytics';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type BoxCreateInput, type BoxInfo, type CashCurrency } from '@lub0v/santa-utils';
import {
  Button,
  Input,
  PicturePicker,
  Select,
  SettingsActionRow,
  SettingsHeader,
  SettingsPage,
  SettingsPanel,
  SettingsSection,
  Typography,
  Switch,
  showDialog,
  showToast,
} from '@src/components';
import { CopyIcon } from '@src/components/icons/CopyIcon';
import { BoxIcon } from '@src/components/icons/BoxIcon';
import { useDuplicateGameDialog } from '@src/pages/BoxesPage/DuplicateGameDialog';
import { TrashIcon } from '@src/components/icons/TrashIcon';
import { FileUpload, type FileUploadHandle } from '@src/components/FileUpload/FileUpload';
import { editBoxInput } from '@src/pages/BoxPage/boxInput';

import { ACCOUNT_BOXES_PATH } from '@src/routing/routes';
import {
  cashCurrencyCodes,
  currencyOptionLabel,
  defaultCashCurrency,
  getBudgetPresetAmounts,
} from '@src/site/currency';
import { santaClient } from '@src/state/client';
import { AppearancePicker } from '@src/pages/NewBoxPage/AppearancePicker';
import './BoxSettingsPage.css';

export interface OrganizerPanelSettingsProps {
  info: BoxInfo;
  showHeader?: boolean;
  section?: 'all' | 'appearance' | 'questionnaires' | 'actions';
  onChanged: () => Promise<void> | void;
}

export function OrganizerPanelSettings({ info, onChanged, showHeader = true, section = 'all' }: OrganizerPanelSettingsProps) {
  const navigate = useNavigate();
  const showDuplicateGameDialog = useDuplicateGameDialog('game_settings');
  const { t } = useTranslation();
  const box = info.box;
  const archived = Boolean(box.isArchived);
  const fileUploadRef = useRef<FileUploadHandle>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [name, setName] = useState(box.name);
  const [cashLimitMin, setCashLimitMin] = useState(
    box.cashLimitMin ? String(box.cashLimitMin) : '',
  );
  const [cashLimit, setCashLimit] = useState(box.cashLimit ? String(box.cashLimit) : '');
  const currencies = cashCurrencyCodes().map((code) => ({
    value: code,
    label: currencyOptionLabel(code, t(`currency.${code}`)),
  }));

  const savedSettings = useRef(editBoxInput(info));
  const saveQueue = useRef(Promise.resolve());

  function save(patch: Partial<BoxCreateInput>) {
    const pending = saveQueue.current
      .then(async () => {
        if (
          archived ||
          Object.entries(patch).every(
            ([key, value]) => savedSettings.current[key as keyof BoxCreateInput] === value,
          )
        )
          return;
        const next = { ...savedSettings.current, ...patch };
        const res = await santaClient.updateBox(next);
        if (!res.ok) {
          showToast(t(res.error?.message || 'newBox.saveFailed'));
          return;
        }
        savedSettings.current = next;
        showToast(t('game.settings.saved'), 'success');
        await onChanged();
      })
      .catch(() => {
        showToast(t('newBox.saveFailed'));
      });
    saveQueue.current = pending;
    return pending;
  }

  function confirmArchive() {
    showDialog({
      title: t(archived ? 'box.organizer.restore.title' : 'box.organizer.archive.title'),
      children: t(archived ? 'box.organizer.restore.body' : 'box.organizer.archive.body'),
      cancel: t('box.cancel'),
      submit: t(archived ? 'box.organizer.restore.submit' : 'box.organizer.archive.submit'),
      onSubmit: async () => {
        const res = await santaClient.updateBox({
          key: box.key,
          name: box.name,
          isArchived: !archived,
        });
        if (!res.ok) {
          showToast(t(archived ? 'box.organizer.restoreFailed' : 'box.organizer.archiveFailed'));
          throw new Error('archive failed');
        }
        await onChanged();
      },
    });
  }

  function confirmDelete() {
    showDialog({
      title: t('game.settings.deleteTitle'),
      children: t('game.settings.deleteBody'),
      confirmationLabel: (
        <Trans
          i18nKey="game.settings.deleteConfirmation"
          components={{ strong: <Typography as="strong" role="body/strong" /> }}
        />
      ),
      testId: 'game-delete-dialog',
      cancel: t('box.cancel'),
      submit: t('game.settings.delete'),
      submitVariant: 'danger',
      onSubmit: async () => {
        const res = await santaClient.deleteBox({ key: box.key });
        if (!res.ok) {
          showToast(t(res.error?.message || 'newBox.saveFailed'));
          throw new Error('delete failed');
        }
        navigate(ACCOUNT_BOXES_PATH);
      },
    });
  }

  return (
    <SettingsPage
      testId="game-settings"
      className={`box-settings organizer-settings--${section}`}
      embedded
      width="full"
      layout="panels"
    >
      {showHeader ? (
        <SettingsHeader title={t('game.settings.title')} subtitle={t('game.settings.subtitle')} />
      ) : null}
      {(section === 'all' || section === 'appearance') && <>
      <OrganizerPanelBoxLink key={`address-${box.key}`} box={box} />
      <SettingsPanel decorated={false}>
        <SettingsSection>
          <AppearancePicker
            selectionContext="settings"
            isPremium={hasPremiumFeature(box, 'boxThemes')}
            theme={box.theme || DEFAULT_GAME_SEASON}
            name={name}
            picture={box.picture}
            logo={box.logo}
            disabled={archived || uploadingPicture}
            onThemeChange={(theme) => void save({ theme })}
          >
            <PicturePicker
              value={box.picture}
              logo={box.logo}
              disabled={archived || uploadingPicture}
              loadingPicture={uploadingPicture ? 'custom_image' : null}
              onValueChange={(picture) => {
                if (picture === 'custom_image') {
                  fileUploadRef.current?.open();
                } else {
                  trackThemeIconSelected(box.theme || DEFAULT_GAME_SEASON, 'settings');
                  void save({ picture, logo: '' });
                }
              }}
              testId="game-settings-picture"
            />
          </AppearancePicker>
          <FileUpload
            ref={fileUploadRef}
            kind="image"
            onChange={(state, file) => {
              if (state === 'selected') {
                setUploadingPicture(true);
              } else if (state === 'uploaded' && file) {
                trackThemeIconSelected(box.theme || DEFAULT_GAME_SEASON, 'settings');
                void save({ picture: 'custom_image', logo: file.url })
                  .catch(() => showToast(t('newBox.saveFailed')))
                  .finally(() => setUploadingPicture(false));
              } else if (state === 'error') {
                setUploadingPicture(false);
                showToast(t('upload.failed'));
              }
            }}
          />
        </SettingsSection>
      </SettingsPanel>
      </>}
      {(section === 'all' || section === 'questionnaires') && <>
      <SettingsPanel decorated={false}>
        <SettingsSection icon={<PencilIcon />} title={t('game.settings.name')}>
          <Input
            value={name}
            onValueChange={setName}
            onBlur={() => {
              const trimmed = name.trim();
              if (trimmed) {
                setName(trimmed);
                void save({ name: trimmed });
              }
            }}
            aria-label={t('game.settings.name')}
            disabled={archived}
            testId="game-settings-name"
          />
        </SettingsSection>
        <SettingsSection icon={<GiftIcon />} title={t('game.settings.budget')} hint={t('game.settings.budgetHint')}>
          <Switch
            checked={Boolean(box.useCashLimit)}
            disabled={archived}
            label={t('game.settings.useBudget')}
            hint={t('game.settings.useBudgetHint')}
            onCheckedChange={(useCashLimit) => {
              if (!useCashLimit) {
                void save({ useCashLimit: false });
                return;
              }
              const cashLimitCurrency =
                savedSettings.current.cashLimitCurrency || defaultCashCurrency();
              const minimum = Number(cashLimitMin) || 0;
              const maximum =
                Number(cashLimit) ||
                Math.max(minimum, getBudgetPresetAmounts(cashLimitCurrency).midMax);
              setCashLimitMin(String(minimum));
              setCashLimit(String(maximum));
              void save({
                useCashLimit: true,
                cashLimitCurrency,
                cashLimitMin: minimum,
                cashLimit: maximum,
              });
            }}
            testId="game-settings-budget"
          />
          {box.useCashLimit ? (
            <>
              <Select
                value={box.cashLimitCurrency || defaultCashCurrency()}
                options={currencies}
                disabled={archived}
                onValueChange={(cashLimitCurrency) =>
                  void save({ cashLimitCurrency: cashLimitCurrency as CashCurrency })
                }
                label={t('newBox.cost.currency')}
              />
              <div className="box-settings-budget-range">
                <Input
                  value={cashLimitMin}
                  onValueChange={setCashLimitMin}
                  onBlur={() => void save({ cashLimitMin: Number(cashLimitMin) || 0 })}
                  numeric
                  disabled={archived}
                  label={t('newBox.cost.min')}
                />
                <Input
                  value={cashLimit}
                  onValueChange={setCashLimit}
                  onBlur={() => void save({ cashLimit: Number(cashLimit) || 0 })}
                  numeric
                  disabled={archived}
                  label={t('newBox.cost.max')}
                />
              </div>
            </>
          ) : null}
        </SettingsSection>
      </SettingsPanel>
      <CustomQuestionBuilder decorated={false} boxKey={box.key} disabled={archived} isPremium={hasPremiumFeature(box, 'boxQuestions')} />
      <SettingsPanel decorated={false}>
        <SettingsSection icon={<CogIcon />} title={t('game.settings.features')} hint={t('game.settings.featuresHint')}>
          <Switch
            checked={Boolean(box.useNames)}
            disabled={archived}
            label={t('game.settings.names')}
            hint={t('game.settings.namesHint')}
            onCheckedChange={(useNames) => void save({ useNames })}
          />
        </SettingsSection>
      </SettingsPanel>
      </>}
      {(section === 'all' || section === 'actions') && <SettingsPanel decorated={false}>
        <SettingsSection
          title={t('game.settings.actions')}
          hint={t('game.settings.actionsHint')}
          icon={<BoxIcon />}
        >
          <SettingsActionRow icon={<CopyIcon />} title={t('boxes.duplicate.title')} hint={t('game.settings.copyHint')}>
            <Button
              variant="secondary"
              size="small"
              className="box-settings-action-button"
              icon={<CopyIcon />}
              disabled={!info.isAdmin}
              onClick={() => showDuplicateGameDialog(info)}
              testId="game-settings-copy"
            >
              {t('boxes.duplicate.title')}
            </Button>
          </SettingsActionRow>
          <SettingsActionRow
            icon={<BoxIcon />}
            title={t(archived ? 'box.organizer.restore.title' : 'box.organizer.archive.title')}
            hint={t(archived ? 'box.organizer.restore.body' : 'box.organizer.archive.body')}
          >
            <Button variant="secondary" size="small" className="box-settings-action-button" icon={<BoxIcon />} onClick={confirmArchive}>
              {t(archived ? 'box.organizer.restore.submit' : 'box.organizer.archive.submit')}
            </Button>
          </SettingsActionRow>
          <SettingsActionRow danger icon={<TrashIcon />} title={t('game.settings.delete')} hint={t('game.settings.deleteBody')}>
            <Button variant="danger-outline" size="small" className="box-settings-action-button" icon={<TrashIcon />} onClick={confirmDelete}>
              {t('game.settings.delete')}
            </Button>
          </SettingsActionRow>
        </SettingsSection>
      </SettingsPanel>}
    </SettingsPage>
  );
}
